import React, { useMemo, useState, useRef, useEffect, useContext } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { GridColumn, Grid, GridToolbar } from '@progress/kendo-react-grid';
import { StatusCodes } from 'http-status-codes';
import { Row, Col, Form } from 'react-bootstrap';

import { API_URL, PACKAGE_TYPES, PLACEMENT_TYPES, ROLES, SAMPLE_TYPES, TEMPERATURE_CONTROL_TYPES } from '../../../../utils/constants';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import OrderProductsApi from '../../../../services/api/OrderProductsApi';
import ModalWrapper from '../../../../components/common/Modal';
import CanvasBox from '../CanvasBox';
import { DatePickerCell } from '../../../../components/common/grid/Cells';
import LabelsApi from '../../../../services/api/LabelsApi';
import ExternalFilter from '../ExternalFilter';
import CalculationService from '../../../../services/logic/CalculationService';
import SecondaryNavigation from '../../../../components/common/navigation/SecondaryNavigation';
import DropdownButton from '../../../../components/common/forms/Button/DropdownButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { grantedForRole } from '~/src/utils/misc';
import MyContext from '~/src/services/context';
import ProductsApi from '~/src/services/api/ProductsApi';
import styled from 'styled-components';
import GtinComponent from '~/src/components/common/GtinComponent';
import DatePickerInputComponent from '~/src/components/common/forms/DatePickerInput/DatePickerInputComponent';
import LinkButton from '~/src/components/common/forms/Button/LinkButton';
import AutocompleteStaticComponent from '~/src/components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { constantsToAutocomplete } from '../../../../utils/misc';
import resetArrow from '../../../../static/img/clockClockwise.svg';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import { InputFilterCell } from '~/src/components/common/grid/InputFilterCell';
import { Colors } from '~/src/scss/common/style';
import DonutChart from '~/src/components/common/chart/DonutChart';
import GridWrapper from '~/src/components/common/GridWrapper';
import arrowDownOrange from '../../../../static/img/arrowDownOrange.svg';
import xIcon from '../../../../static/img/xIcon.svg';
import { dateToNull } from 'svila-common';

const cols = {
  productName: 'Product name',
  original: 'Original',
  minusP: '-P',
  minusL: '-L',
  units: '#Units',
  plusP: '+P',
  plusL: '+L',
  value: 'Value',
  placement: 'Placement',
  pallets: 'Pallets',
  sample: 'Sample',
  productionDate: 'Production date',
  expiry: 'Expiry',
  label: 'Label',
  action: 'Action',
};

export default function ProductsCalculations(): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chosenPallet, setChosenPallet] = useState(null);
  const [canvasData, setCanvasData] = useState(null);
  const [palletCounts, setPalletCounts] = useState({});
  const [totals, setTotals] = useState({});
  const [IsPalletBreakdownVisible, setIsPalletBreakdownVisible] = useState(false);

  const history = useHistory();
  const { id } = useParams<{ id: string; }>();
  const context = useContext(MyContext);
  const stateRef = useRef(null);
  const calculationService = useRef(null);
  const stateRefNoCalc = useRef(null);
  const labelOptions = useRef([]);

  const url = `${API_URL}/orders/order-products/calculator/get-list/${id}`;
  const StatefullGrid = useMemo(() => withState(Grid, url, { pageable: false }), []);

  const urlNoCalc = `${API_URL}/orders/order-products/calculator/get-list-no-calculations/${id}`;
  const StatefullGridNoCalc = useMemo(() => withState(Grid, urlNoCalc, { pageable: false }), []);

  const handleUpdateData = async (rowData, ref) => {
    const res = await OrderProductsApi.updateCalculator(rowData.id, rowData);
    if (res.status === StatusCodes.OK) {
      if (ref) {
        ref.current.updateOneRow(res.data);
      } else {
        stateRef.current.updateOneRow(res.data);
      }
      getVisualizationData();
      getTotals();
    }
  };

  const handlePalletClick = (index) => {
    setChosenPallet(index);
    setIsModalOpen(true);
  };

  // const barCodeCell = (row, type) => {
  //   const cuPack = row.dataItem?.product?.packagedProducts.find((x) => x.pack.type === type);
  //   return <td>{cuPack.gtin}</td>;
  // };

  const PLCell = (row) => {
    const { dataItem } = row;

    const sumQty = dataItem.quantityDu + dataItem[row.field];

    return (
      <td style={{ textAlign: 'center' }}>
        <LinkButton
          onClick={(e): void => {
            dataItem.quantityDu = sumQty;
            handleUpdateData(dataItem);
          }}
          underline={true}
          disabled={dataItem[row.field] === 0}
          title={`${row.field.indexOf('Plus') !== -1 && dataItem[row.field] !== 0 ? '+' : ''}${dataItem[row.field]
            }`}
        />
      </td>
    );
  };

  const QuantityCell = (row, ref) => {
    const { dataItem } = row;

    return (
      <td
        style={{
          padding: '5px',
          height: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TextInputComponent
            type="number"
            cols={13}
            height={25}
            className="form-control"
            onChange={(e) => {
              dataItem.quantityDu = e.target.valueAsNumber;
              ref.current.updateOneRow(dataItem);
            }}
            onBlur={(e) => {
              handleUpdateData(dataItem, ref);
            }}
            value={dataItem.quantityDu}
          />
          {dataItem.originalQuantityDu !== dataItem.quantityDu && (
            <WrapperQuantityRemoveBtn>
              <BorderlessButton
                onClick={(e) => {
                  dataItem.quantityDu = dataItem.originalQuantityDu;
                  handleUpdateData(dataItem);
                }}
                icon={resetArrow}
              />
            </WrapperQuantityRemoveBtn>
          )}
        </div>
      </td>
    );
  };

  const ProductNameCell = (row, ref) => {
    const { dataItem } = row;
    const cuPack = dataItem.product?.packagedProducts.find((x) => x.pack.type === PACKAGE_TYPES.CONSUMER_UNIT);
    const duPack = dataItem.product?.packagedProducts.find(
      (x) => x.pack.type === PACKAGE_TYPES.DISTRIBUTION_UNIT && x.smallerUnitId === cuPack.id,
    );
    const gtins = [];
    if (cuPack && cuPack.gtin) {
      gtins.push(`cu: ${cuPack.gtin}`);
    }
    if (duPack && duPack.gtin) {
      gtins.push(`du: ${duPack.gtin}`);
    }
    return (
      // <td style={{ borderLeft: '10px solid', borderColor: dataItem.color }}>
      <td>
        <WrapperText color={dataItem.color}>
          <div style={{ marginBottom: '4px', textAlign: 'left' }}>
            <TitleLink to={`/products/edit/${dataItem.product.genericProduct.id}`} target="_blank">
              {dataItem.product.genericProduct.name}
            </TitleLink>
          </div>
          <GtinWrapper>
            <GtinComponent text="CU" code={cuPack.gtin} />
            <GtinComponent text="DU" code={duPack.gtin} />
          </GtinWrapper>
        </WrapperText>
        <Row style={{ marginTop: 10, marginBottom: 14 }}>
          <Col style={{ marginLeft: '10px' }}>
            <Form.Group>
              <StyledControl
                as="select"
                defaultValue={dataItem.placement}
                onChange={(e) => {
                  dataItem.placement = e.target.value;
                  handleUpdateData(dataItem, ref);
                }}
              >
                <option value="">None</option>
                {Object.values(PLACEMENT_TYPES).map((val, index) => (
                  <option key={index} value={val}>
                    {val}
                  </option>
                ))}
              </StyledControl>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <StyledControl
                as="select"
                defaultValue={dataItem.sample}
                onChange={(e) => {
                  dataItem.sample = e.target.value;
                  handleUpdateData(dataItem, ref);
                }}
              >
                {Object.values(SAMPLE_TYPES).map((val, index) => (
                  <option key={index} value={val}>
                    {val}
                  </option>
                ))}
              </StyledControl>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <StyledControl
                as="select"
                defaultValue={dataItem.labelId}
                onChange={(e) => {
                  dataItem.labelId = e.target.value;
                  handleUpdateData(dataItem, ref);
                }}
              >
                <option value="">None</option>
                {labelOptions.current.map((option) => (
                  <option key={option.id} value={option.id}>{`${option.width}x${option.height}`}</option>
                ))}
              </StyledControl>
            </Form.Group>
          </Col>
        </Row>
        {!duPack.grossWeight && (
          <Row>
            <RedNote>No Du GrossWeight</RedNote>
          </Row>
        )}
      </td>
    );
  };

  const PriceCell = (row, ref) => {
    const { dataItem } = row;
    const percentage =
      dataItem.totalCostCu && dataItem.pricePerCu
        ? Math.round((dataItem.pricePerCu / dataItem.totalCostCu - 1) * 10000) / 10000
        : 0;

    return (
      <td style={{ textAlign: 'center' }}>
        <TextInputComponent
          type="number"
          cols={12}
          height={25}
          className="form-control"
          onChange={(e) => {
            dataItem.pricePerCu = e.target.valueAsNumber;
            ref.current.updateOneRow(dataItem);
          }}
          onBlur={(e) => {
            handleUpdateData(dataItem, ref);
          }}
          value={dataItem.pricePerCu}
        />
        <br />
        {`${percentage}%`}
      </td>
    );
  };

  const ProductionExpiryCell = (row, ref) => {
    const { dataItem, field } = row;

    return (
      <td style={{ margin: '0 5px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <DatePickerCell
              format="dd-MM-yyyy"
              width="110px"
              value={dataItem[field]}
              onChange={(e) => {
                dataItem[field] = dateToNull(e.target.value);
                handleUpdateData(dataItem, ref);
              }}
              height={25}
            />
            <div style={{ marginBottom: '16px' }} />
            <DatePickerCell
              format="dd-MM-yyyy"
              width="110px"
              value={dataItem.expiry}
              onChange={(e) => {
                dataItem.expiry = dateToNull(e.target.value);
                handleUpdateData(dataItem, ref);
              }}
            />
          </div>
          <BorderlessButton
            onClick={(e) => {
              dataItem.expiry = null;
              dataItem[field] = null;
              handleUpdateData(dataItem, ref);
            }}
            icon={xIcon}
            iconSize={25}
          />
        </div>
      </td>
    );
  };

  const DisplayPalletCount = () => {
    return Object.keys(palletCounts).map((key) => (
      <div key={key}>
        <h2>{`${key}:${palletCounts[key].rounded}`}</h2> <p>{palletCounts[key].fraction}</p>
      </div>
    ));
  };

  const SupplierCell = (row, ref) => {
    const { dataItem } = row;

    return (
      <td style={{ padding: 5 }}>
        <StyledControl
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
          }}
          as="select"
          defaultValue={dataItem.supplierId}
          onChange={(e) => {
            dataItem.supplierId = e.target.value;
            handleUpdateData(dataItem, ref);
          }}
        >
          <option value="">None</option>
          {dataItem.product?.supplierProducts.map((item) => (
            <option key={item.id} value={item.supplierId}>
              {item.supplier?.name}
            </option>
          ))}
        </StyledControl>
      </td>
    );
  };

  const CostCell = (row, ref) => {
    const { dataItem } = row;

    const currentSupplier =
      dataItem.product?.supplierProducts.find((item) => item.supplierId === dataItem.supplierId) || {};

    const transportCost = dataItem.defaultTemperatureControl === TEMPERATURE_CONTROL_TYPES.REEFER
      ? currentSupplier.supplier?.reeferTransportCostPerPallet ?? 0
      : currentSupplier.supplier?.nonReeferTransportCostPerPallet ?? 0;

    return (
      <td>
        {currentSupplier && (
          <>
            {`Supplier Cost: ${dataItem.supplierAdjustedCostPerCu}`}
            <br />
            {`Transport In: ${transportCost}`}
            <br />
            <b>{`Cost To Bg: ${dataItem.supplierAdjustedCostPerCu + dataItem.transportCostPerCuIn} `}</b>
            <br />
            {`Transport Out: ${dataItem.transportCostOut} `}
            <br />
            <b>{`CFR: ${dataItem.totalCostCu}`}</b>
            <br />
          </>
        )}
      </td>
    );
  };

  const CalculatablesCell = (row, ref) => {
    const { dataItem } = row;

    const calcs = dataItem.calculatables || {};

    return (
      <td>
        <ul>
          {calcs.hasLayerInfo === false ? <li>Layer Info</li> : ''}
          {calcs.hasDuSizes === false ? <li>Du Sizes</li> : ''}
          {calcs.hasPallet === false ? <li>Pallet Info</li> : ''}
          {calcs.calculated === false ? <li>Algorithm problem</li> : ''}
          {calcs.hasLogistics === false ? <li>Logistics problem</li> : ''}
          {calcs.possibleByArea === false ? <li>Area problem</li> : ''}
        </ul>
      </td>
    );
  };

  const handleFilterChange = (values, ref) => {
    ref.current.externalFilterChange(values);
  };

  const handleBack = () => {
    history.push(`/orders/details/${id}`);
  };

  const getVisualizationData = async () => {
    const result = await OrderProductsApi.getVisualizationList(id);
    if (result.status === StatusCodes.OK) {
      setCanvasData(result.data);
      calculationService.current.loadVisualizationData(result.data);
      setPalletCounts(calculationService.current.getPalletCounts());
    }
  };

  const getDropdownOptions = async () => {
    const result = await LabelsApi.getList();
    if (result.status === StatusCodes.OK) {
      labelOptions.current = result.data.data;
    }
  };

  const getTotals = async () => {
    var result = await OrderProductsApi.getTotals(id);
    if (result.status === StatusCodes.OK) {
      setTotals(result.data);
    }
  };

  useEffect(() => {
    calculationService.current = new CalculationService();
    getVisualizationData();
    getDropdownOptions();
    getTotals();
  }, []);

  return (
    <WhitePad isFluid>
      {/* <div style={{ position: 'relative' }}> */}
      <SecondaryNavigation />
      <TitleComponent
        onLeftHandler={(): void => {
          history.push('/orders-list');
        }}
        title="Order Products"
      />
      <ContainerInfoWrapper className="row">
        {/* <div className="col-sm-3" style={{ width: '20%', maxWidth: '20%' }}>
          <WrapperChart>
            <TitleChart>Container Full</TitleChart>
            <DonutChart />
            <StyleP color="#EF7D00">75% - Lorem ipsum</StyleP>
            <StyleP color="#642B26">75% - Lorem ipsum</StyleP>
            <StyleP color="#D81F21">75% - Lorem ipsum</StyleP>
          </WrapperChart>
        </div> */}
        <StyleColumn className="col-sm-4">
          <DisplayPalletCount />
        </StyleColumn>
        <StyleColumn className="col-sm-4">
          <div>
            <h2>Pallets Free:</h2>
          </div>
          <div>
            <h2>Pallets Overload:</h2>
          </div>
        </StyleColumn>
        <StyleColumn className="col-sm-4">
          <div>
            <h2>Gross Weight:</h2> <p>{totals.grossWeight ? `${totals.grossWeight}kg` : ``}</p>
          </div>
        </StyleColumn>
        <StyleColumn className="col-sm-4">
          <div>
            <h2>Price:</h2>
            <p>{` €${totals.subtotalPrice}`}</p>
          </div>
          <div>
            <br />
            <h2>VAT:</h2>
            <p>{` €${totals.subtotalVat} (${totals.vat}%)`}</p>
          </div>
          <div>
            <br />
            <h2>Total:</h2>
            <p>{` €${totals.totalPrice}`}</p>
          </div>
        </StyleColumn>
      </ContainerInfoWrapper>
      {IsPalletBreakdownVisible && (
        <Row style={{ background: '#F4F4F4', padding: '40px' }}>
          {canvasData?.pallets.map((pallet, index) => (
            <Col
              key={index}
              style={{
                flexGrow: 0,
                flexShrink: 0,
                cursor: 'pointer',
                flexBasis: 130,
                width: 110,
                padding: 5,
                margin: 5,
                backgroundColor: '#f2f2f2',
              }}
              onClick={() => handlePalletClick(index)}
            >
              <CanvasBox data={pallet} isSmall />
            </Col>
          ))}
        </Row>
      )}

      <WrapperDropdownButton>
        <DropdownButton
          onClick={() => setIsPalletBreakdownVisible((prevState) => !prevState)}
          IsPalletBreakdownVisible={IsPalletBreakdownVisible}
          title={`${IsPalletBreakdownVisible ? 'Hide ' : 'Show '}
        Pallet Visualization`}
        />
      </WrapperDropdownButton>
      <GridWrapper textAlign="center">
        <StatefullGrid ref={stateRef} className="sv-compact">
          <GridToolbar style={{ padding: '0px !important' }}>
            <ExternalFilter onSubmit={(values) => handleFilterChange(values, stateRef)} />
          </GridToolbar>
          <GridColumn
            field="product.genericProduct.name"
            title={cols.productName}
            cell={(row) => ProductNameCell(row, stateRef)}
            width={321}
            columns={{ headerAlign: 'center' }}
          />
          <GridColumn
            field="originalQuantityDU"
            title="#DU Client"
            filterable={false}
            encoding={false}
            width={50}
            textAlign="center"
          />
          <GridColumn field="palletMinus" title="-P" cell={PLCell} filterable={false} width={50} />
          <GridColumn field="layerMinus" title="-L" cell={PLCell} filterable={false} width={50} />
          <GridColumn
            field="quantityDU"
            title="#DU"
            cell={(row) => QuantityCell(row, stateRef)}
            filterable={false}
            width={80}
          />
          <GridColumn field="layerPlus" title="+L" cell={PLCell} filterable={false} width={50} />
          <GridColumn field="palletPlus" title="+P" cell={PLCell} filterable={false} width={50} />

          <GridColumn field="totalCUs" title=" Total CU" filterable={false} width={70} />

          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn
              field="pricePerCU"
              title="Price/CU (€)"
              cell={(row) => PriceCell(row, stateRef)}
              width={150}
            />
          )}

          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn field="totalPrice" title="Total Price" width={150} />
          )}
          <GridColumn field="pallets" title={cols.pallets} width={120} />
          <GridColumn
            field="productionDate"
            title="Production Date / Expiry"
            filterable={false}
            cell={(row) => ProductionExpiryCell(row, stateRef)}
            width={140}
          />
          <GridColumn
            field="supplier"
            title="Supplier"
            cell={(row) => SupplierCell(row, stateRef)}
            filterable={false}
            width={230}
          />
          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn
              field="cost"
              title="Cost / Transport / Total (€)"
              cell={(row) => CostCell(row, stateRef)}
              width={200}
            />
          )}
        </StatefullGrid>
      </GridWrapper>
      <div className="mb-5" />
      <GridWrapper textAlign="center">
        <StatefullGridNoCalc ref={stateRefNoCalc} className="sv-compact">
          <GridToolbar>
            <ExternalFilter onSubmit={(values) => handleFilterChange(values, stateRefNoCalc)} />
          </GridToolbar>
          <GridColumn
            field="calculatables"
            title="Missing:"
            filterable={false}
            cell={(row) => CalculatablesCell(row, stateRefNoCalc)}
            width={160}
          />
          <GridColumn
            field="product.genericProduct.name"
            title={cols.productName}
            filterable={false}
            cell={(row) => ProductNameCell(row, stateRefNoCalc)}
            width={500}
          />
          <GridColumn
            field="originalQuantityDU"
            title="#DU Client"
            filterable={false}
            encoding={false}
            width={50}
          />
          <GridColumn
            field="quantityDU"
            title="#DU"
            cell={(row) => QuantityCell(row, stateRefNoCalc)}
            filterable={false}
            width={80}
          />
          <GridColumn field="quantityCU" title="#CU" filterable={false} width={60} />
          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn
              field="pricePerCU"
              title="Price/CU (€)"
              cell={(row) => PriceCell(row, stateRefNoCalc)}
              filterable={false}
              width={100}
            />
          )}
          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn field="totalPrice" title="Total Price" filterable={false} width={100} />
          )}

          <GridColumn field="pallets" title={cols.pallets} filterable={false} width={60} />
          <GridColumn
            field="productionDate"
            title="Production Date / Expiry"
            filterable={false}
            cell={(row) => ProductionExpiryCell(row, stateRefNoCalc)}
            width={150}
          />
          <GridColumn
            field="supplier"
            title="Supplier"
            cell={(row) => SupplierCell(row, stateRefNoCalc)}
            filterable={false}
            width={230}
          />
          {grantedForRole([ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SALES], context.user?.role) && (
            <GridColumn
              field="cost"
              title="Cost / Transport / Total (€)"
              cell={(row) => CostCell(row, stateRefNoCalc)}
              width={200}
            />
          )}
        </StatefullGridNoCalc>
      </GridWrapper>

      <ModalWrapper
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(!isModalOpen);
          setChosenPallet(null);
        }}
      >
        <AlignModal>
          {chosenPallet !== null && (
            <CanvasBox
              onClickFunc={() => {
                setIsModalOpen(!isModalOpen);
              }}
              data={canvasData.pallets[chosenPallet]}
            />
          )}
        </AlignModal>
      </ModalWrapper>
      {/* </div> */}
    </WhitePad>
  );
}

const AlignModal = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyleP = styled.p`
  font-size: 14px;
  font-weight: 700;
  padding: 0;
  margin-bottom: 7px;
  color: ${(props) => props.color}!important;
`;

const WrapperChart = styled.div`
  background: #f4f4f4;
  padding: 15px;
  border-radius: 10px;
`;

const TitleChart = styled.h3`
  color: #a7a7a7;
  font-size: 18px;
  margin-bottom: 18px;
  font-weight: 700;
  padding: 0;
`;

const StyleColumn = styled.div`
  border-right: 1px solid #eeeeee;
  width: 25% !important;
  max-width: 25%;
`;

const ContainerInfoWrapper = styled.div`
  margin-top: 50px;
  margin-bottom: 50px;
  h2 {
    color: #a7a7a7;
    font-size: 18px;
    line-height: 25px;
    font-weight: 700;
    padding: 0;
    margin-right: 5px;
    text-transform: none;
  }
  p {
    color: #642b26;
    font-size: 18px;
    line-height: 25px;
    font-weight: 600;
  }
  text-align: center;
  div {
    div {
      display: flex;
      flex-direction: column;
      text-align: center;
    }
  }
  div:last-child {
    div {
      display: flex;
      flex-direction: row;
      border-right: 0px;
    }
  }
`;

const RedNote = styled(Col)`
  color: red;
`;

const GtinWrapper = styled.div`
  display: flex;
`;

const TitleLink = styled(Link)`
  color: #343434;
  font-size: 14px;
  line-height: 17px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const WrapperText = styled.div`
  position: relative;
  margin-left: 20px;
  margin-top: 9px;
  paddong-top: 10px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: -7px;
    left: -18px;
    width: 13px;
    height: 49px;
    background-color: ${(props) => {
    return props.color;
  }};
    border-radius: 10px;
  }
`;

const StyledControl = styled(Form.Control)`
  position: relative;
  appearance: none;
  border-radius: 200px;
  padding: 3px;
  border: 1px solid #eeeeee !important;
  height: 25px;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  background-image: url(${arrowDownOrange});
  background-repeat: no-repeat;
  background-position: right 0.5em top 55%, 0 0;
  background-size: 0.65em auto;
  padding-right: 20px;
  padding-left: 7px;
  &::before {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: red;
  }
  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0;
  }
`;

const WrapperDropdownButton = styled.div`
  transform: translateY(-50%);
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 78px;
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    z-index: 1;
    background-color: #eeeeee;
  }
`;

const WrapperQuantityRemoveBtn = styled.div`
  position: absolute;
  bottom: 7px;
  left: 50%;
  transform: translateX(-50%);
`;
